import React, { useEffect, useState } from "react";

import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import "./AdminVolatilityForexTransaction.css";


const AdminVolatilityForexTransaction = () => {

  // =====================================================
  // STATE
  // =====================================================

  const [transactions, setTransactions] = useState([]);

  const [loading, setLoading] = useState(true);

  const [updatingId, setUpdatingId] = useState(null);


  // =====================================================
  // LOAD BOTH COLLECTIONS
  //
  // volatilityforex
  // volatilityotherbalance
  // =====================================================

  useEffect(() => {

    setLoading(true);

    let forexData = [];

    let otherBalanceData = [];


    const mergeTransactions = () => {

      const combined = [
        ...forexData,
        ...otherBalanceData,
      ];


      const sorted = [...combined].sort(
        (a, b) => {

          const aTime =
            a.createdAt?.seconds ||
            a.timestamp?.seconds ||
            0;

          const bTime =
            b.createdAt?.seconds ||
            b.timestamp?.seconds ||
            0;

          return bTime - aTime;

        }
      );


      setTransactions(sorted);

      setLoading(false);

    };


    // =====================================================
    // VOLATILITYFOREX
    // =====================================================

    const unsubscribeForex =
      onSnapshot(

        collection(
          db,
          "volatilityforex"
        ),

        (snapshot) => {

          forexData =
            snapshot.docs.map(
              (item) => ({

                id: item.id,

                ...item.data(),

                sourceCollection:
                  "volatilityforex",

              })
            );


          mergeTransactions();

        },

        (error) => {

          console.error(
            "Failed to load volatilityforex:",
            error
          );

          forexData = [];

          mergeTransactions();

        }

      );


    // =====================================================
    // VOLATILITY OTHER BALANCE
    // =====================================================

    const unsubscribeOtherBalance =
      onSnapshot(

        collection(
          db,
          "volatilityotherbalance"
        ),

        (snapshot) => {

          otherBalanceData =
            snapshot.docs.map(
              (item) => ({

                id: item.id,

                ...item.data(),

                sourceCollection:
                  "volatilityotherbalance",

              })
            );


          mergeTransactions();

        },

        (error) => {

          console.error(
            "Failed to load volatilityotherbalance:",
            error
          );

          otherBalanceData = [];

          mergeTransactions();

        }

      );


    return () => {

      unsubscribeForex();

      unsubscribeOtherBalance();

    };

  }, []);


  // =====================================================
  // GET AMOUNT TO CREDIT
  //
  // =====================================================
  //
  // VOLATILITYFOREX:
  //
  // 1. CRYPTO
  //    amount = USDT
  //
  // 2. MOBILE MONEY
  //    amount = TZS
  //    usdtAmount = USDT
  //
  // VOLATILITYOTHERBALANCE:
  //
  //    usdtAmount = USDT
  //
  // =====================================================

  const getCreditAmount = (
    transaction
  ) => {

    const source =
      transaction?.sourceCollection;


    // =====================================================
    // VOLATILITYFOREX
    // =====================================================

    if (
      source === "volatilityforex"
    ) {

      // ================================================
      // MOBILE MONEY
      //
      // Example:
      //
      // amount = 10000 TZS
      // currency = TZS
      // usdtAmount = 3.76
      //
      // Credit = 3.76
      // ================================================

      const isMobileMoney =
        transaction?.depositMethod ===
          "mobile" ||

        transaction?.walletType ===
          "mobile_money" ||

        String(
          transaction?.currency || ""
        ).toUpperCase() ===
          "TZS";


      if (
        isMobileMoney
      ) {

        const usdtAmount =
          Number(
            transaction?.usdtAmount
          );


        if (
          Number.isFinite(
            usdtAmount
          ) &&
          usdtAmount > 0
        ) {

          return usdtAmount;

        }


        return 0;

      }


      // ================================================
      // DIRECT CRYPTO
      //
      // Hizi ziko tayari USDT/crypto.
      //
      // amount ndiyo amount ya credit.
      // ================================================

      const amount =
        Number(
          transaction?.amount
        );


      if (
        Number.isFinite(
          amount
        ) &&
        amount > 0
      ) {

        return amount;

      }


      // ================================================
      // FALLBACK KWA CRYPTO IKIWA AMOUNT HAIPO
      // ================================================

      const usdtAmount =
        Number(
          transaction?.usdtAmount
        );


      if (
        Number.isFinite(
          usdtAmount
        ) &&
        usdtAmount > 0
      ) {

        return usdtAmount;

      }


      return 0;

    }


    // =====================================================
    // VOLATILITYOTHERBALANCE
    //
    // HII COLLECTION INATUMIA USDTAMOUNT
    // =====================================================

    if (
      source ===
      "volatilityotherbalance"
    ) {

      const usdtAmount =
        Number(
          transaction?.usdtAmount
        );


      if (
        Number.isFinite(
          usdtAmount
        ) &&
        usdtAmount > 0
      ) {

        return usdtAmount;

      }


      return 0;

    }


    return 0;

  };


  // =====================================================
  // GET ORIGINAL AMOUNT
  // =====================================================

  const getOriginalAmount = (
    transaction
  ) => {

    const amount =
      Number(
        transaction?.amount || 0
      );


    if (
      Number.isFinite(
        amount
      )
    ) {

      return amount;

    }


    return 0;

  };


  // =====================================================
  // GET ORIGINAL CURRENCY
  // =====================================================

  const getOriginalCurrency = (
    transaction
  ) => {

    return (
      transaction?.currency ||
      transaction?.depositCurrency ||
      "USDT"
    );

  };


  // =====================================================
  // GET CREDIT CURRENCY
  // =====================================================

  const getCreditCurrency = (
    transaction
  ) => {

    return (
      transaction?.receiveCurrency ||
      "USDT"
    );

  };


  // =====================================================
  // GET SOURCE NAME
  // =====================================================

  const getSourceName = (
    transaction
  ) => {

    if (
      transaction?.sourceCollection ===
      "volatilityotherbalance"
    ) {

      return "Other Balance";

    }


    return "VolatilityForex";

  };


  // =====================================================
  // GET SOURCE CLASS
  // =====================================================

  const getSourceClass = (
    transaction
  ) => {

    if (
      transaction?.sourceCollection ===
      "volatilityotherbalance"
    ) {

      return "other-balance";

    }


    return "forex";

  };


  // =====================================================
  // UPDATE STATUS
  // =====================================================

  const updateStatus = async (
    transaction,
    newStatus
  ) => {

    const transactionId =
      transaction.id;


    const sourceCollection =
      transaction.sourceCollection ||
      "volatilityforex";


    const updatingKey =
      `${sourceCollection}-${transactionId}`;


    setUpdatingId(
      updatingKey
    );


    try {

      // =================================================
      // PREVIOUS STATUS
      // =================================================

      const previousStatus =
        String(
          transaction.status ||
          "pending"
        ).toLowerCase();


      // =================================================
      // NEW STATUS
      // =================================================

      const normalizedNewStatus =
        String(
          newStatus
        ).toLowerCase();


      // =================================================
      // SAME STATUS
      // =================================================

      if (
        previousStatus ===
        normalizedNewStatus
      ) {

        return;

      }


      // =================================================
      // USERNAME
      // =================================================

      if (
        !transaction.username
      ) {

        alert(
          "Transaction has no username."
        );

        return;

      }


      // =================================================
      // TRANSACTION REFERENCE
      // =================================================

      const transactionRef =
        doc(
          db,
          sourceCollection,
          transactionId
        );


      // =================================================
      // SUCCESS
      // =================================================

      if (
        normalizedNewStatus ===
        "success"
      ) {

        // ===============================================
        // PREVENT DOUBLE CREDIT
        // ===============================================

        const shouldCreditBalance =
          previousStatus !==
          "success";


        // ===============================================
        // GET AMOUNT TO CREDIT
        // ===============================================

        const creditAmount =
          getCreditAmount(
            transaction
          );


        // ===============================================
        // VALIDATE
        // ===============================================

        if (
          !Number.isFinite(
            creditAmount
          ) ||
          creditAmount <= 0
        ) {

          alert(
            "Invalid USDT amount for this transaction."
          );

          return;

        }


        // ===============================================
        // FIND USER
        // ===============================================

        const usersRef =
          collection(
            db,
            "users"
          );


        const userQuery =
          query(
            usersRef,
            where(
              "username",
              "==",
              transaction.username
            )
          );


        const userSnapshot =
          await getDocs(
            userQuery
          );


        // ===============================================
        // USER NOT FOUND
        // ===============================================

        if (
          userSnapshot.empty
        ) {

          alert(
            "User account was not found."
          );

          return;

        }


        // ===============================================
        // USER DOCUMENT
        // ===============================================

        const userDoc =
          userSnapshot.docs[0];


        const userData =
          userDoc.data();


        // ===============================================
        // CURRENT BALANCE
        // ===============================================

        const currentBalance =
          Number(
            userData.usdtBalanceOther ||
            0
          );


        // ===============================================
        // NEW BALANCE
        // ===============================================

        const newBalance =
          currentBalance +
          creditAmount;


        // ===============================================
        // UPDATE TRANSACTION
        // ===============================================

        await updateDoc(
          transactionRef,
          {

            status:
              "success",

            updatedAt:
              serverTimestamp(),

          }
        );


        // ===============================================
        // CREDIT USER
        // ===============================================

        if (
          shouldCreditBalance
        ) {

          await updateDoc(
            doc(
              db,
              "users",
              userDoc.id
            ),
            {

              usdtBalanceOther:
                newBalance,

            }
          );


          // =============================================
          // CONSOLE
          // =============================================

          console.log(
            "======================================"
          );

          console.log(
            "USDT DEPOSIT SUCCESS"
          );

          console.log(
            `Collection: ${sourceCollection}`
          );

          console.log(
            `Username: ${transaction.username}`
          );

          console.log(
            `Amount Paid: ${getOriginalAmount(
              transaction
            )} ${getOriginalCurrency(
              transaction
            )}`
          );

          console.log(
            `Amount Credited: ${creditAmount} USDT`
          );

          console.log(
            `Previous Balance: ${currentBalance} USDT`
          );

          console.log(
            `New Balance: ${newBalance} USDT`
          );

          console.log(
            "Balance Field: users.usdtBalanceOther"
          );

          console.log(
            "======================================"
          );

        }


        // ===============================================
        // SUCCESS MESSAGE
        // ===============================================

        alert(
          `SUCCESS!\n\n` +
          `User: ${transaction.username}\n\n` +
          `USDT Added: $${creditAmount.toFixed(
            2
          )}\n\n` +
          `Previous Balance: $${currentBalance.toFixed(
            2
          )}\n\n` +
          `New Balance: $${newBalance.toFixed(
            2
          )}\n\n` +
          `Balance: users.usdtBalanceOther`
        );


        return;

      }


      // =====================================================
      // PROCESSING / PENDING / REJECTED / EXPIRED
      // =====================================================

      await updateDoc(
        transactionRef,
        {

          status:
            normalizedNewStatus,

          updatedAt:
            serverTimestamp(),

        }
      );

    }

    catch (error) {

      console.error(
        "Status update failed:",
        error
      );


      alert(
        "Status update failed. Check console."
      );

    }

    finally {

      setUpdatingId(
        null
      );

    }

  };


  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (
    timestamp
  ) => {

    if (
      !timestamp
    ) {

      return "-";

    }


    try {

      if (
        typeof timestamp.toDate ===
        "function"
      ) {

        return timestamp
          .toDate()
          .toLocaleString();

      }


      if (
        timestamp.seconds
      ) {

        return new Date(
          timestamp.seconds * 1000
        ).toLocaleString();

      }


      return "-";

    }

    catch {

      return "-";

    }

  };


  // =====================================================
  // STATUS CLASS
  // =====================================================

  const getStatusClass = (
    status
  ) => {

    return String(
      status ||
      "pending"
    )
      .toLowerCase()
      .replace(
        /\s+/g,
        "-"
      );

  };


  // =====================================================
  // SUMMARY
  // =====================================================

  const totalTransactions =
    transactions.length;


  const successTransactions =
    transactions.filter(
      (tx) =>
        String(
          tx.status || ""
        ).toLowerCase() ===
        "success"
    );


  const processingTransactions =
    transactions.filter(
      (tx) =>
        String(
          tx.status || ""
        ).toLowerCase() ===
        "processing"
    );


  const pendingTransactions =
    transactions.filter(
      (tx) =>
        String(
          tx.status || ""
        ).toLowerCase() ===
        "pending"
    );


  const rejectedTransactions =
    transactions.filter(
      (tx) =>
        String(
          tx.status || ""
        ).toLowerCase() ===
        "rejected"
    );


  // =====================================================
  // TOTAL SUCCESSFUL USDT
  // =====================================================

  const totalSuccessfulUsdt =
    successTransactions.reduce(
      (
        total,
        transaction
      ) => {

        return (
          total +
          getCreditAmount(
            transaction
          )
        );

      },
      0
    );


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <div
      className="admin-volatility-container"
    >

      {/* =================================================
          HEADER
      ================================================= */}

      <div
        className="admin-volatility-header"
      >

        <div>

          <div
            className="admin-volatility-title"
          >
            VolatilityForex
          </div>

          <div
            className="admin-volatility-subtitle"
          >
            Crypto & Other Balance Transactions
          </div>

        </div>


        <div
          className="admin-volatility-live"
        >

          <span
            className="admin-live-dot"
          />

          LIVE

        </div>

      </div>


      {/* =================================================
          SUMMARY
      ================================================= */}

      <div
        className="admin-volatility-summary"
      >

        <div
          className="volatility-summary-card"
        >

          <span
            className="summary-label"
          >
            Total
          </span>

          <strong
            className="summary-value"
          >
            {totalTransactions}
          </strong>

        </div>


        <div
          className="volatility-summary-card success-card"
        >

          <span
            className="summary-label"
          >
            Success
          </span>

          <strong
            className="summary-value"
          >
            {
              successTransactions.length
            }
          </strong>

        </div>


        <div
          className="volatility-summary-card processing-card"
        >

          <span
            className="summary-label"
          >
            Processing
          </span>

          <strong
            className="summary-value"
          >
            {
              processingTransactions.length
            }
          </strong>

        </div>


        <div
          className="volatility-summary-card pending-card"
        >

          <span
            className="summary-label"
          >
            Pending
          </span>

          <strong
            className="summary-value"
          >
            {
              pendingTransactions.length
            }
          </strong>

        </div>


        <div
          className="volatility-summary-card rejected-card"
        >

          <span
            className="summary-label"
          >
            Rejected
          </span>

          <strong
            className="summary-value"
          >
            {
              rejectedTransactions.length
            }
          </strong>

        </div>


        <div
          className="volatility-summary-card amount-card"
        >

          <span
            className="summary-label"
          >
            Successful USDT
          </span>

          <strong
            className="summary-value"
          >

            $
            {
              totalSuccessfulUsdt.toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                }
              )
            }

          </strong>

          <small>
            users.usdtBalanceOther
          </small>

        </div>

      </div>


      {/* =================================================
          BALANCE NOTICE
      ================================================= */}

      <div
        className="balance-notice"
      >

        <div
          className="balance-notice-icon"
        >
          ◎
        </div>

        <div>

          <strong>
            Balance Destination
          </strong>

          <span>

            VolatilityForex crypto
            ina-credit{" "}

            <b>
              amount
            </b>

            kama USDT.

            {" "}

            Mobile Money
            ina-credit{" "}

            <b>
              usdtAmount
            </b>

            {" "}kwenye{" "}

            <b>
              users.usdtBalanceOther
            </b>

          </span>

        </div>

      </div>


      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (

        <div
          className="volatility-loading"
        >

          <div
            className="loading-spinner"
          />

          <span>
            Inapakia transactions...
          </span>

        </div>

      ) : (

        <div
          className="volatility-table-wrapper"
        >

          <table
            className="volatility-table"
          >

            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  Source
                </th>

                <th>
                  Username
                </th>

                <th>
                  Type
                </th>

                <th>
                  Amount Paid
                </th>

                <th>
                  Balance To Credit
                </th>

                <th>
                  Method
                </th>

                <th>
                  Transaction ID
                </th>

                <th>
                  Date
                </th>

                <th>
                  Status
                </th>

              </tr>

            </thead>


            <tbody>

              {transactions.length === 0 ? (

                <tr>

                  <td
                    colSpan="10"
                    className="empty-transactions"
                  >

                    <div>

                      <span>
                        ◇
                      </span>

                      <p>
                        No VolatilityForex
                        transactions found.
                      </p>

                    </div>

                  </td>

                </tr>

              ) : (

                transactions.map(
                  (
                    transaction,
                    index
                  ) => {

                    const originalAmount =
                      getOriginalAmount(
                        transaction
                      );


                    const creditAmount =
                      getCreditAmount(
                        transaction
                      );


                    const sourceName =
                      getSourceName(
                        transaction
                      );


                    const sourceClass =
                      getSourceClass(
                        transaction
                      );


                    const updatingKey =
                      `${transaction.sourceCollection}-${transaction.id}`;


                    return (

                      <tr
                        key={
                          updatingKey
                        }
                      >

                        {/* =================================
                            NUMBER
                        ================================= */}

                        <td>

                          <span
                            className="row-number"
                          >
                            {index + 1}
                          </span>

                        </td>


                        {/* =================================
                            SOURCE
                        ================================= */}

                        <td>

                          <span
                            className={
                              `source-badge ${sourceClass}`
                            }
                          >

                            {
                              sourceName
                            }

                          </span>

                        </td>


                        {/* =================================
                            USERNAME
                        ================================= */}

                        <td>

                          <strong
                            className="username-cell"
                          >

                            {
                              transaction.username ||
                              "-"
                            }

                          </strong>

                        </td>


                        {/* =================================
                            TYPE
                        ================================= */}

                        <td>

                          <span
                            className="coin-badge"
                          >

                            {
                              transaction.type ||
                              "deposit"
                            }

                          </span>

                        </td>


                        {/* =================================
                            AMOUNT PAID
                        ================================= */}

                        <td>

                          <div
                            className="amount-cell"
                          >

                            <strong>

                              {
                                originalAmount.toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits:
                                      0,
                                    maximumFractionDigits:
                                      8,
                                  }
                                )
                              }

                            </strong>

                            <span>

                              {
                                getOriginalCurrency(
                                  transaction
                                )
                              }

                            </span>

                          </div>

                        </td>


                        {/* =================================
                            BALANCE TO CREDIT
                        ================================= */}

                        <td>

                          <div
                            className="balance-credit-cell"
                          >

                            <strong>

                              +$
                              {
                                creditAmount.toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits:
                                      2,
                                    maximumFractionDigits:
                                      8,
                                  }
                                )
                              }

                            </strong>

                            <span>

                              {
                                getCreditCurrency(
                                  transaction
                                )
                              }

                            </span>

                          </div>

                        </td>


                        {/* =================================
                            METHOD
                        ================================= */}

                        <td>

                          <span
                            className="network-cell"
                          >

                            {
                              transaction.provider ||
                              transaction.paymentMethod ||
                              transaction.method ||
                              transaction.network ||
                              "-"
                            }

                          </span>

                        </td>


                        {/* =================================
                            TRANSACTION ID
                        ================================= */}

                        <td>

                          <span
                            className="transaction-id-cell"
                            title={
                              transaction.transactionId ||
                              transaction.id
                            }
                          >

                            {
                              transaction.transactionId ||
                              transaction.id ||
                              "-"
                            }

                          </span>

                        </td>


                        {/* =================================
                            DATE
                        ================================= */}

                        <td>

                          <span
                            className="date-cell"
                          >

                            {
                              formatDate(
                                transaction.createdAt ||
                                transaction.timestamp
                              )
                            }

                          </span>

                        </td>


                        {/* =================================
                            STATUS
                        ================================= */}

                        <td>

                          <select
                            disabled={
                              updatingId ===
                              updatingKey
                            }
                            value={
                              transaction.status ||
                              "pending"
                            }
                            onChange={
                              (e) =>
                                updateStatus(
                                  transaction,
                                  e.target.value
                                )
                            }
                            className={
                              `volatility-status-select ${getStatusClass(
                                transaction.status
                              )}`
                            }
                          >

                            <option value="pending">
                              Pending
                            </option>

                            <option value="processing">
                              Processing
                            </option>

                            <option value="success">
                              Success
                            </option>

                            <option value="rejected">
                              Rejected
                            </option>

                            <option value="expired">
                              Expired
                            </option>

                          </select>

                        </td>

                      </tr>

                    );

                  }

                )

              )}

            </tbody>

          </table>

        </div>

      )}

    </div>

  );

};


export default AdminVolatilityForexTransaction;